/** Быстрая постановка в план производства (порт productionQuickPlanHtml, стр. 6374):
 * канал + неделя (от текущей) + количество → инкремент плана; при close_control_mp
 * атомарно закрывает позицию контроля (фикс мёртвого controlKey оригинала). */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useMemo, useState } from "react";
import { toast } from "sonner";

import * as planningApi from "@/api/orders-planning";
import { extractErrorMessage } from "@/lib/api-errors";
import { salesPlanChannels, type PlanChannelInfo } from "./plans";
import { currentProductionWeekIndex, productionYearWeeks } from "./production";

export function QuickPlanWidget({
  article,
  channels,
  defaultChannel,
  closeControlMp,
  disabled,
}: {
  article: string;
  channels: planningApi.PlanChannel[];
  defaultChannel?: string;
  closeControlMp?: "wb" | "ozon" | "ym";
  disabled?: boolean;
}): React.ReactElement {
  const qc = useQueryClient();
  const year = new Date().getFullYear();
  const weekOptions = useMemo(() => {
    const current = currentProductionWeekIndex(year);
    const all = productionYearWeeks(year).filter((w) => w.index >= current);
    return all.length ? all : productionYearWeeks(year);
  }, [year]);
  const channelInfos: PlanChannelInfo[] = useMemo(() => salesPlanChannels(channels), [channels]);
  const [channel, setChannel] = useState(defaultChannel ?? "ozon");
  const [week, setWeek] = useState(weekOptions[0]?.key ?? "");
  const [qty, setQty] = useState("");

  const mut = useMutation({
    mutationFn: planningApi.quickPlan,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["orders", "production"] });
      if (r.control_closed) qc.invalidateQueries({ queryKey: ["orders", "control"] });
      setQty("");
      toast.success(
        r.control_closed ? "Поставлено в план, контроль закрыт" : "Поставлено в план",
      );
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось поставить в план")),
  });

  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <select
        className="h-6 max-w-20 rounded border bg-background px-0.5 text-[10px]"
        value={channel}
        disabled={disabled}
        onChange={(e) => setChannel(e.target.value)}
      >
        {channelInfos.map((c) => (
          <option key={c.code} value={c.code}>{c.label}</option>
        ))}
      </select>
      <select
        className="h-6 rounded border bg-background px-0.5 text-[10px]"
        value={week}
        disabled={disabled}
        onChange={(e) => setWeek(e.target.value)}
      >
        {weekOptions.map((w) => (
          <option key={w.key} value={w.key}>Н{w.index}</option>
        ))}
      </select>
      <input
        type="number"
        min={1}
        value={qty}
        disabled={disabled}
        onChange={(e) => setQty(e.target.value)}
        className="h-6 w-14 rounded border bg-background px-1 text-right text-[10px]"
        placeholder="шт"
      />
      <button
        type="button"
        disabled={disabled || !qty || !week || mut.isPending}
        className="h-6 rounded border px-1.5 text-[10px] font-medium hover:bg-accent disabled:opacity-50"
        onClick={() =>
          mut.mutate({
            year: Number(week.slice(0, 4)),
            article,
            week_key: week,
            channel_code: channel,
            qty: Math.max(1, Math.round(Number(qty))),
            close_control_mp: closeControlMp ?? null,
          })
        }
      >
        OK
      </button>
    </div>
  );
}
